import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    studentApi,
    type Student,
    type StudentFilters,
    type CreateStudentData,
    type UpdateStudentData,
} from "@/api/student";

export const useStudentStore = defineStore("student", () => {
    // State
    const students = ref<Student[]>([]);
    const currentStudent = ref<Student | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const gender = ref("");
    const currentClassId = ref("");
    const status = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeStudents = computed(() =>
        students.value.filter((student) => student.current_class_id),
    );

    const studentsByClass = computed(() => {
        const grouped: Record<string, Student[]> = {};
        students.value.forEach((student) => {
            const className = student.current_class?.name || "Unassigned";
            if (!grouped[className]) grouped[className] = [];
            grouped[className].push(student);
        });
        return grouped;
    });

    const studentsByGender = computed(() => {
        const grouped: Record<string, Student[]> = {};
        students.value.forEach((student) => {
            const gender = student.gender || "Unknown";
            if (!grouped[gender]) grouped[gender] = [];
            grouped[gender].push(student);
        });
        return grouped;
    });

    // Actions
    const fetchStudents = async (filters: StudentFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await studentApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                gender: gender.value || undefined,
                current_class_id: currentClassId.value || undefined,
                status: status.value || undefined,
                ...filters,
            });

            students.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch students";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchStudent = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentStudent.value = (await studentApi.getById(id)).data;
            return currentStudent.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch student";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createStudent = async (data: CreateStudentData) => {
        loading.value = true;
        error.value = null;

        try {
            const newStudent = await studentApi.create(data);
            // Refresh the list to ensure consistency
            await fetchStudents();
            return newStudent;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create student";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateStudent = async (id: string, data: UpdateStudentData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedStudent = await studentApi.update(id, data);
            const index = students.value.findIndex((s) => s.id === id);
            if (index !== -1) {
                students.value[index] = updatedStudent.data;
            }
            if (currentStudent.value?.id === id) {
                currentStudent.value = updatedStudent.data;
            }
            return updatedStudent;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update student";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteStudent = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await studentApi.delete(id);
            students.value = students.value.filter((s) => s.id !== id);
            if (currentStudent.value?.id === id) {
                currentStudent.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete student";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchStudentsByClass = async (classId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classStudents = await studentApi.getByClass(classId);
            console.info(classStudents);
            return classStudents;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch students by class";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const importStudents = async (file: FormData) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await studentApi.importStudents(file);

            // Refresh the students list after successful import
            await fetchStudents();

            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to import students";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
        fetchStudents();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchStudents();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        gender.value = "";
        currentClassId.value = "";
        status.value = "";
        fetchStudents();
    };

    return {
        // State
        students,
        currentStudent,
        loading,
        error,
        page,
        perPage,
        search,
        gender,
        currentClassId,
        status,
        meta,

        // Getters
        activeStudents,
        studentsByClass,
        studentsByGender,

        // Actions
        fetchStudents,
        fetchStudent,
        createStudent,
        updateStudent,
        deleteStudent,
        fetchStudentsByClass,
        importStudents,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
